import { fetchConfig } from '@theme/utilities';
import { WishlistUpdateEvent, WishlistErrorEvent } from '@theme/events';

/**
 * Guest wishlist, backed by localStorage, with merge-on-login into the account wishlist.
 *
 * Storage/network are treated as best-effort: the in-memory `#ids` set is always the
 * source of truth for what's currently rendered, so a toggle is instant regardless of
 * whether localStorage or the network request behind it succeeds. Failures roll the
 * affected product back to its previous state and surface a WishlistErrorEvent.
 */

const STORAGE_KEY = 'pravaa:wishlist';
const NUDGE_DISMISSED_KEY = 'pravaa:wishlist-nudge-dismissed';
const NUDGE_THRESHOLD = 3;
const PROXY_BASE = '/apps/wishlist';

/** @param {string} key */
function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 * @returns {boolean} whether the write succeeded
 */
function safeSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (_error) {
    return false;
  }
}

/** @returns {string[]} */
function readGuestWishlist() {
  const raw = safeGet(STORAGE_KEY);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (_error) {
    return [];
  }
}

/**
 * @param {string[]} ids
 * @returns {boolean} whether the write succeeded
 */
function writeGuestWishlist(ids) {
  return safeSet(STORAGE_KEY, JSON.stringify(ids));
}

function clearGuestWishlist() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_error) {
    /* no-op: nothing to clean up if storage isn't available */
  }
}

/**
 * Minimal, dependency-free toast/banner used for wishlist error and nudge messaging.
 * Reuses one host element so repeated messages don't stack up.
 *
 * @param {object} options
 * @param {string} options.message
 * @param {'error' | 'nudge'} options.variant
 * @param {{ label: string, href: string }} [options.action]
 * @param {() => void} [options.onDismiss]
 * @param {number} [options.autoDismissMs]
 */
function showWishlistBanner({ message, variant, action, onDismiss, autoDismissMs }) {
  if (typeof document === 'undefined') return;

  let host = document.querySelector('#wishlist-banner-host');
  if (!host) {
    host = document.createElement('div');
    host.id = 'wishlist-banner-host';
    host.setAttribute('aria-live', 'polite');
    document.body.appendChild(host);
  }

  host.innerHTML = '';

  const banner = document.createElement('div');
  banner.className = `wishlist-banner wishlist-banner--${variant}`;

  const text = document.createElement('span');
  text.className = 'wishlist-banner__message';
  text.textContent = message;
  banner.appendChild(text);

  if (action) {
    const link = document.createElement('a');
    link.className = 'wishlist-banner__action';
    link.href = action.href;
    link.textContent = action.label;
    banner.appendChild(link);
  }

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'wishlist-banner__dismiss';
  dismiss.setAttribute('aria-label', 'Dismiss');
  dismiss.textContent = '×';
  banner.appendChild(dismiss);

  const remove = () => {
    banner.classList.add('wishlist-banner--leaving');
    setTimeout(() => banner.remove(), 200);
  };

  dismiss.addEventListener('click', () => {
    remove();
    onDismiss?.();
  });

  host.appendChild(banner);

  requestAnimationFrame(() => banner.classList.add('wishlist-banner--visible'));

  if (autoDismissMs) {
    setTimeout(remove, autoDismissMs);
  }
}

export class Wishlist {
  /** @type {Set<string>} */
  static #ids = new Set();
  /** @type {string | null} */
  static #customerId = null;
  /** @type {string | null} */
  static #accountUrl = null;
  /** @type {boolean} */
  static #ready = false;
  /** @type {Promise<void> | null} */
  static #readyPromise = null;
  /** @type {Set<string>} product ids currently mid-request */
  static #pending = new Set();
  /** @type {Map<string, boolean>} latest desired state per product id, queued while a request is in flight */
  static #desired = new Map();
  /** @type {boolean} */
  static #storageWarningShown = false;

  static get isGuest() {
    return !this.#customerId;
  }

  /**
   * Boots the wishlist for the current page: loads the right source of truth and, for a
   * freshly logged-in customer with leftover guest data, merges it into their account.
   * Safe to call more than once; only the first call does anything.
   *
   * @param {string | number | null} customerId
   * @param {string | null} [accountUrl] - Used as the nudge banner's sign-up/login link
   */
  static init(customerId, accountUrl) {
    if (this.#readyPromise) return this.#readyPromise;

    this.#customerId = customerId ? String(customerId) : null;
    this.#accountUrl = accountUrl ?? null;

    this.#readyPromise = (async () => {
      if (this.#customerId) {
        await this.#loadRemote();
        await this.#mergeGuestIntoAccountIfNeeded();
      } else {
        this.#ids = new Set(readGuestWishlist());
        this.#checkStorageAvailability();
      }

      this.#ready = true;
      this.#notify();

      this.#pruneMissingProducts();
    })();

    return this.#readyPromise;
  }

  /**
   * Drops wishlist entries whose product no longer exists (deleted or re-created products),
   * so the header count matches what the wishlist page can actually show. Only a definite
   * 404 removes an entry; network errors leave the wishlist untouched. Runs once per session.
   */
  static async #pruneMissingProducts() {
    const SESSION_KEY = 'pravaa:wishlist-pruned';
    try {
      if (window.sessionStorage.getItem(SESSION_KEY)) return;
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch (_error) {
      // sessionStorage unavailable: still prune, just without the once-per-session guard
    }

    const ids = this.getAll();
    const missing = await Promise.all(
      ids.map(async (id) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(id)}.js`, {
            headers: { Accept: 'application/json' },
          });
          return response.status === 404 ? id : null;
        } catch (_error) {
          return null;
        }
      })
    );

    for (const id of missing) {
      if (!id || !this.#ids.has(id)) continue;
      this.#setLocal(id, false);
      this.#queueSync(id, false);
    }
  }

  /**
   * @param {string | number} productId
   */
  static has(productId) {
    return this.#ids.has(String(productId));
  }

  /** @returns {string[]} */
  static getAll() {
    return [...this.#ids];
  }

  /** @returns {boolean} whether the initial load (guest storage or account fetch) has completed */
  static get hasLoaded() {
    return this.#ready;
  }

  /**
   * Toggles a product's wishlist state. Updates the in-memory/rendered state instantly
   * (optimistic) and kicks off the background storage/network sync; callers don't need
   * to await anything to get correct-looking UI.
   *
   * @param {string | number} productId
   * @returns {boolean} the new optimistic state
   */
  static toggle(productId) {
    const id = String(productId);
    const nextState = !this.#ids.has(id);

    this.#setLocal(id, nextState);
    this.#queueSync(id, nextState);

    return nextState;
  }

  /**
   * @param {string} id
   * @param {boolean} added
   */
  static #setLocal(id, added) {
    if (added) this.#ids.add(id);
    else this.#ids.delete(id);

    if (this.isGuest) {
      writeGuestWishlist([...this.#ids]);
    }

    this.#notify(id, added);

    if (added) this.#maybeShowNudge();
  }

  /**
   * Queues the desired state for a product and syncs it in the background. Rapid
   * repeated toggles collapse into a single in-flight request plus, at most, one
   * follow-up request reflecting whatever the latest click landed on.
   *
   * @param {string} id
   * @param {boolean} added
   */
  static async #queueSync(id, added) {
    this.#desired.set(id, added);

    if (this.#pending.has(id)) return;
    this.#pending.add(id);

    try {
      while (this.#desired.has(id)) {
        const target = /** @type {boolean} */ (this.#desired.get(id));
        this.#desired.delete(id);

        try {
          if (!this.isGuest) {
            await this.#syncRemote(id, target);
          }
        } catch (_error) {
          // Roll back the optimistic update for this product and let the shopper know.
          this.#setLocal(id, !target);
          document.dispatchEvent(
            new WishlistErrorEvent(id, "We couldn't update your wishlist. Please try again.")
          );
          showWishlistBanner({
            variant: 'error',
            message: "We couldn't update your wishlist. Please try again.",
            autoDismissMs: 4000,
          });
        }
      }
    } finally {
      this.#pending.delete(id);
    }
  }

  /**
   * @param {string} id
   * @param {boolean} added
   */
  static async #syncRemote(id, added) {
    const response = await fetch(
      added ? `${PROXY_BASE}/items` : `${PROXY_BASE}/items/${encodeURIComponent(id)}`,
      added
        ? fetchConfig('json', { body: JSON.stringify({ productId: id }) })
        : { method: 'DELETE', headers: { Accept: 'application/json' } }
    );

    if (!response.ok) {
      throw new Error(`Wishlist request failed with status ${response.status}`);
    }
  }

  static async #loadRemote() {
    try {
      const response = await fetch(PROXY_BASE, { method: 'GET', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Failed to load wishlist (${response.status})`);

      const data = await response.json();
      this.#ids = new Set((data.productIds ?? []).map(String));
    } catch (error) {
      // We couldn't confirm the server's state. Fail closed (empty) rather than risk
      // showing guest/local items as if they were actually saved to the account.
      console.error('[wishlist] Failed to load account wishlist', error);
      this.#ids = new Set();
    }
  }

  static async #mergeGuestIntoAccountIfNeeded() {
    const guestIds = readGuestWishlist();
    if (guestIds.length === 0) return;

    try {
      const response = await fetch(`${PROXY_BASE}/merge`, fetchConfig('json', { body: JSON.stringify({ productIds: guestIds }) }));

      if (!response.ok) throw new Error(`Merge failed with status ${response.status}`);

      const data = await response.json();
      this.#ids = new Set((data.productIds ?? [...this.#ids, ...guestIds]).map(String));

      // Only clear the guest copy once the server has confirmed the merge, so a failed
      // request can safely retry on the next page load instead of losing data.
      clearGuestWishlist();
    } catch (error) {
      console.error('[wishlist] Failed to merge guest wishlist into account', error);
    }
  }

  static #checkStorageAvailability() {
    if (this.#storageWarningShown) return;
    if (safeSet('pravaa:wishlist-test', '1')) return;

    this.#storageWarningShown = true;
    showWishlistBanner({
      variant: 'error',
      message: "Your browser is blocking storage, so your wishlist won't be saved after you leave this page.",
      autoDismissMs: 6000,
    });
  }

  /**
   * @param {string} [changedProductId]
   * @param {boolean} [added]
   */
  static #notify(changedProductId, added) {
    document.dispatchEvent(new WishlistUpdateEvent([...this.#ids], changedProductId, added));
  }

  static #maybeShowNudge() {
    if (!this.isGuest) return;
    if (safeGet(NUDGE_DISMISSED_KEY)) return;
    if (this.#ids.size < NUDGE_THRESHOLD) return;

    showWishlistBanner({
      variant: 'nudge',
      message: 'Like what you see? Create an account to keep your wishlist saved across devices.',
      action: this.#accountUrl ? { label: 'Create account', href: this.#accountUrl } : undefined,
      onDismiss: () => safeSet(NUDGE_DISMISSED_KEY, '1'),
    });
  }
}
